import { useCallback, useEffect, useRef, useState } from "react";

export interface UseSubmittingMutationOptions<TResult> {
  onSuccess?: (result: TResult) => void | Promise<void>;
  onError?: (error: unknown) => void;
  /** Refresh contract: refetch function(s) the CALLER owns (its load(),
   *  a parent-provided callback, or auth.refresh). Awaited after fn
   *  resolves, BEFORE onSuccess. Failures surface via onError. */
  refresh?: Array<() => void | Promise<unknown>> | (() => void | Promise<unknown>);
}

export interface SubmittingMutation<TArgs extends unknown[], TResult> {
  /** Runs the mutation. Re-entrant calls while pending are a no-op
   * (double-submit prevention). Errors are captured, surfaced via
   * `error`/`onError`, and never thrown to the caller. */
  submit: (...args: TArgs) => Promise<TResult | undefined>;
  pending: boolean;
  error: unknown;
  resetError: () => void;
}

/** Shared in-flight state for portal/staff submit actions: one pending
 * flag, guaranteed single-flight, and error capture that re-enables the
 * action. After `fn` resolves, every `refresh` function is awaited (so
 * server-derived state is refetched) before `onSuccess` runs; a refresh
 * rejection follows the error path like an `fn` failure. Pair with
 * `SubmitButton` + `SubmittingHint`. */
export function useSubmittingMutation<TArgs extends unknown[], TResult>(
  fn: (...args: TArgs) => Promise<TResult>,
  options?: UseSubmittingMutationOptions<TResult>
): SubmittingMutation<TArgs, TResult> {
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const inFlight = useRef(false);
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  // Keep the latest fn/options without re-creating submit (stable identity
  // so callers can safely list it in effect deps).
  const fnRef = useRef(fn);
  fnRef.current = fn;
  const optionsRef = useRef(options);
  optionsRef.current = options;

  const submit = useCallback(async (...args: TArgs): Promise<TResult | undefined> => {
    if (inFlight.current) return undefined;
    inFlight.current = true;
    setPending(true);
    setError(null);
    try {
      const result = await fnRef.current(...args);
      const refresh = optionsRef.current?.refresh;
      if (refresh) {
        const refreshFns = Array.isArray(refresh) ? refresh : [refresh];
        for (const refreshFn of refreshFns) {
          await refreshFn();
        }
      }
      await optionsRef.current?.onSuccess?.(result);
      return result;
    } catch (err) {
      if (mounted.current) setError(err);
      optionsRef.current?.onError?.(err);
      return undefined;
    } finally {
      inFlight.current = false;
      if (mounted.current) setPending(false);
    }
  }, []);

  const resetError = useCallback(() => setError(null), []);

  return { submit, pending, error, resetError };
}
